import { useEffect, useState } from "react";
import { DEFAULT_ACCENT_COLOR } from "./commons/constants";
import { GiftProvider } from "./context/GiftProvider";
import GiftCardScreens from "./screens/GiftCardScreens";
import {
  GiftCardAppliedCardType,
  GiftCardApplyType,
  GiftCardConfigType,
  GiftCardDeleteType,
  GiftCardRechargeWalletType,
  GiftCardType,
  GiftCardUiModalConfigType,
  GiftCardValidateCodeType,
} from "./types";

// Matches the sheet's own `data-[state=closed]:duration-300` (sheetVariants in
// @shadcn/components/ui/sheet.tsx). Unmounting on this delay instead of on `isOpen` itself is what
// lets the close transition actually play instead of the whole tree being torn out mid-animation.
const SHEET_CLOSE_DURATION_MS = 300;

export interface GiftCardPopupProps {
  isOpen: boolean;
  onClose: () => void;
  container?: HTMLElement | null;
  coupons: GiftCardConfigType[];
  appliedCards?: GiftCardAppliedCardType[];
  maxUsage?: number;
  /**
   * Applies the card the shopper filled in. The entered code and redeem amount live in
   * `GiftProvider`, which the host is outside of, so the active card is handed over here.
   */
  handleApply: GiftCardApplyType;
  isApplyLoading?: boolean;
  /** Validates the entered card and resolves with its redeemable balance. */
  validateCode: GiftCardValidateCodeType;
  /**
   * Tops a wallet-shaped provider up with a voucher and resolves with the amount loaded.
   * Omit it and the top-up screen is unreachable — there is nothing to load with.
   */
  rechargeWallet?: GiftCardRechargeWalletType;
  /**
   * Detaches one applied card, identified by its `transaction_id`. Omit it and applied cards
   * render without a remove button.
   */
  handleDelete?: GiftCardDeleteType;
  /**
   * Called instead of switching to a provider the shopper picked, when that provider's own
   * `unavailability_reasons` says it can't be redeemed as-is. Omit it and an unavailable
   * provider is selected like any other.
   */
  onProviderUnavailable?: (card: GiftCardType) => void;
  accentColor?: string;
  uiModalConfig?: GiftCardUiModalConfigType;
  showCloseButton?: boolean;
  /**
   * Formats every amount the applied list shows. Pass the app's currency formatter — the fallback
   * is a bare rupee prefix.
   */
  formatAmount?: (value: number) => string;
  isFullyCovered?: boolean;
  /**
   * Marks this as the merchant dashboard's preview render, unlocking dashboard-only features in the
   * screens. The checkout leaves it unset — the default is off.
   */
  dashboardOnly?: boolean;
}

const GiftCardPopup = ({
  isOpen,
  onClose,
  container,
  coupons,
  appliedCards,
  maxUsage,
  handleApply,
  rechargeWallet,
  handleDelete,
  onProviderUnavailable,
  validateCode,
  isApplyLoading = false,
  accentColor = DEFAULT_ACCENT_COLOR,
  uiModalConfig,
  showCloseButton,
  formatAmount,
  isFullyCovered,
  dashboardOnly = false,
}: GiftCardPopupProps) => {
  const [isMounted, setIsMounted] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      return;
    }
    const timeout = setTimeout(() => setIsMounted(false), SHEET_CLOSE_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [isOpen]);

  if (!isMounted) return <></>;

  return (
    <GiftProvider
      coupons={coupons}
      appliedCards={appliedCards}
      maxUsage={maxUsage}
      validateCode={validateCode}
      handleApply={handleApply}
      rechargeWallet={rechargeWallet}
      handleDelete={handleDelete}
      onProviderUnavailable={onProviderUnavailable}
      dashboardOnly={dashboardOnly}
    >
      <GiftCardScreens
        isOpen={isOpen}
        onClose={onClose}
        container={container}
        accentColor={accentColor}
        uiModalConfig={uiModalConfig}
        showCloseButton={showCloseButton}
        formatAmount={formatAmount}
        isFullyCovered={isFullyCovered}
      />
    </GiftProvider>
  );
};

export default GiftCardPopup;
